import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const screenshots = [
  {
    url: 'https://customer-assets.emergentagent.com/job_440533c4-5232-4051-9f88-4212552e250e/artifacts/42l76b3k_Captura%20de%20tela%202026-02-16%20135423.png',
    title: 'Visão Geral Completa',
    description: 'Acompanhe todas as suas métricas financeiras em tempo real'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_440533c4-5232-4051-9f88-4212552e250e/artifacts/p1g9d3py_Captura%20de%20tela%202026-02-16%20135510.png',
    title: 'Gestão de Receitas',
    description: 'Gerencie suas fontes de renda de forma inteligente'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_440533c4-5232-4051-9f88-4212552e250e/artifacts/ds4tqher_Captura%20de%20tela%202026-02-16%20135555.png',
    title: 'Controle de Gastos',
    description: 'Visualize e categorize seus gastos automaticamente'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_440533c4-5232-4051-9f88-4212552e250e/artifacts/n76ia4n4_Captura%20de%20tela%202026-02-16%20135616.png',
    title: 'Metas Financeiras',
    description: 'Defina e acompanhe seus objetivos com IA'
  }
];

export const ShowcaseSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % screenshots.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  };

  return (
    <section ref={ref} className="py-20 sm:py-32 relative">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Conheça o{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
              FinanceAI
            </span>
            {' '}por dentro
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Uma interface intuitiva e poderosa para transformar sua relação com o dinheiro
          </p>
        </motion.div>

        {/* Carousel */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-6xl mx-auto"
        >
          <div className="relative">
            {/* Main Image */}
            <div className="relative aspect-video rounded-2xl overflow-hidden bg-card border border-border shadow-2xl">
              <img
                src={screenshots[currentIndex].url}
                alt={screenshots[currentIndex].title}
                className="w-full h-full object-cover"
              />
              
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
              
              {/* Info overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h3 className="text-2xl font-bold mb-2">{screenshots[currentIndex].title}</h3>
                <p className="text-muted-foreground">{screenshots[currentIndex].description}</p>
              </div>
            </div>

            {/* Navigation Buttons */}
            <Button
              onClick={prevSlide}
              variant="outline"
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-background/80 backdrop-blur-sm hover:bg-background border-border hover:border-primary transition-all"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>
            <Button
              onClick={nextSlide}
              variant="outline"
              size="icon"
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-background/80 backdrop-blur-sm hover:bg-background border-border hover:border-primary transition-all"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          </div>

          {/* Thumbnails */}
          <div className="flex justify-center gap-4 mt-8 flex-wrap">
            {screenshots.map((screenshot, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`relative w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden border-2 transition-all ${
                  currentIndex === index
                    ? 'border-primary shadow-glow scale-110'
                    : 'border-border hover:border-primary/50 opacity-60 hover:opacity-100'
                }`}
              >
                <img
                  src={screenshot.url}
                  alt={screenshot.title}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
